import { createReducer, on } from '@ngrx/store';
import { getMediaPreviewUri } from '@pixacare/domain';
import { FlattenedSequence, FlattenedTelemonitoring } from '@pixacare/dtos';
import { getNextPagedCollection } from '@shared/models/helpers/pagination-helper';
import { toArchivable } from '../archivable.adapter';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { sequencesActions } from '../sequences/sequences.actions';
import { StoreTelemonitoring } from './models/store-telemonitoring';
import { telemonitoringsActions } from './telemonitorings.actions';
import { initialTelemonitoringsState, TelemonitoringsState } from './telemonitorings.state';

const flattenedTelemonitoringToStoreTelemonitoring = (
  fTelemonitoring: FlattenedTelemonitoring,
): StoreTelemonitoring => ({
  ...fTelemonitoring.telemonitoring,
  sequenceIds: fTelemonitoring.sequences?.map((sequence) => sequence.sequenceInstance.id) || null,
  previewMedia: fTelemonitoring.previewMedia,
});

const flattenedTelemonitoringsToStoreTelemonitorings = (
  fTelemonitorings: FlattenedTelemonitoring[],
): { [telemonitoringId: number]: StoreTelemonitoring } =>
  Object.fromEntries(
    fTelemonitorings.map((fTelemonitoring) => [
      fTelemonitoring.telemonitoring.id,
      flattenedTelemonitoringToStoreTelemonitoring(fTelemonitoring),
    ]),
  );

const addSequenceIds = (
  state: TelemonitoringsState,
  telemonitoringId: number,
  sequenceIds: number[],
): TelemonitoringsState => ({
  ...state,
  telemonitorings: {
    ...state.telemonitorings,
    data: {
      ...state.telemonitorings.data,
      [telemonitoringId]: {
        ...state.telemonitorings.data[telemonitoringId],
        sequenceIds: [...state.telemonitorings.data[telemonitoringId].sequenceIds, ...sequenceIds],
        lastActivity: new Date(Date.now()),
      },
    },
  },
});

const getThumbnailFromSequences = (sequences: FlattenedSequence[]): string => {
  const sortedSequences = [...sequences].sort(
    (a, b) => b.sequenceInstance.createdOn.getTime() - a.sequenceInstance.createdOn.getTime(),
  );
  return getMediaPreviewUri(sortedSequences[0]?.medias[0]);
};

export const telemonitoringsReducer = createReducer(
  initialTelemonitoringsState,

  on(lifeCycleActions.resetState, telemonitoringsActions.resetTelemonitoringsState, () => initialTelemonitoringsState),

  on(telemonitoringsActions.getNextUserTelemonitorings, (state, { options: { reset } }) => ({
    ...state,
    telemonitorings: {
      ...state.telemonitorings,
      context: {
        ...state.telemonitorings.context,
        isNextPageLoading: true,
      },
      ...(reset && { sorted: [], data: null }),
    },
    ...(reset && { isLoaded: false }),
  })),

  on(
    telemonitoringsActions.getNextUserTelemonitoringsSuccess,
    (state, { reset, collection }): TelemonitoringsState => ({
      ...state,
      telemonitorings: getNextPagedCollection(
        {
          currentCollection: state.telemonitorings,
          nextCollection: collection,
        },
        {
          reset,
          sortPredicate: (entityId1, entityId2, entities) =>
            entities[entityId2].lastActivity?.getTime() - entities[entityId1].lastActivity?.getTime(),
          dataReducer: flattenedTelemonitoringsToStoreTelemonitorings,
          dataIdentifier: 'telemonitoring.id',
        },
      ),
      isLoaded: true,
    }),
  ),

  on(telemonitoringsActions.addTelemonitoring, (state: TelemonitoringsState, { telemonitoring }) => ({
    ...state,
    telemonitorings: {
      ...state.telemonitorings,
      data: {
        ...state.telemonitorings.data,
        [telemonitoring.id]: telemonitoring,
      },
      sorted: [telemonitoring.id, ...state.telemonitorings.sorted],
    },
  })),

  on(telemonitoringsActions.getTelemonitoring, (state: TelemonitoringsState) => ({
    ...state,
    isLoaded: false,
  })),

  on(telemonitoringsActions.getTelemonitoringSuccess, (state: TelemonitoringsState, { telemonitoring }) => ({
    ...state,
    isLoaded: true,
    telemonitorings: {
      ...state.telemonitorings,
      data: {
        ...state.telemonitorings.data,
        [telemonitoring.telemonitoring.id]: flattenedTelemonitoringToStoreTelemonitoring(telemonitoring),
      },
    },
  })),

  on(telemonitoringsActions.deleteTelemonitoringSuccess, (state: TelemonitoringsState, { telemonitoringId }) => ({
    ...state,
    telemonitorings: {
      ...state.telemonitorings,
      data: Object.fromEntries(
        Object.values(state.telemonitorings.data)
          .filter((tlm) => tlm.id !== telemonitoringId)
          .map((tlm) => [tlm.id, tlm]),
      ),
      sorted: [...state.telemonitorings.sorted.filter((id) => telemonitoringId !== id)],
    },
  })),

  on(
    telemonitoringsActions.updateTelemonitoringPatient,
    (state: TelemonitoringsState, { patientId, targetPatientId }) => ({
      ...state,
      telemonitorings: {
        ...state.telemonitorings,
        data: {
          ...state.telemonitorings.data,
          ...Object.fromEntries(
            Object.values(state.telemonitorings.data)
              .filter((telemonitoring) => telemonitoring.patientId === patientId)
              .map((telemonitoring) => [telemonitoring.id, { ...telemonitoring, patientId: targetPatientId }]),
          ),
        },
      },
    }),
  ),

  on(
    telemonitoringsActions.archiveTelemonitoringSuccess,
    telemonitoringsActions.unarchiveTelemonitoringSuccess,
    (state: TelemonitoringsState, { telemonitoring }) => ({
      ...state,
      telemonitorings: {
        ...state.telemonitorings,
        data: {
          ...state.telemonitorings.data,
          [telemonitoring.id]: {
            ...state.telemonitorings.data[telemonitoring.id],
            ...toArchivable(telemonitoring),
          },
        },
      },
    }),
  ),

  on(
    telemonitoringsActions.sendDepositReminderSuccess,
    (state: TelemonitoringsState, { telemonitoringId, telemonitoring }) => ({
      ...state,
      telemonitorings: {
        ...state.telemonitorings,
        data: {
          ...state.telemonitorings.data,
          [telemonitoringId]: {
            ...state.telemonitorings.data[telemonitoringId],
            ...telemonitoring,
          },
        },
      },
    }),
  ),

  on(sequencesActions.createSequenceSuccess, (state: TelemonitoringsState, { sequence }) => {
    if (!sequence.sequenceInstance.telemonitoringId) {
      return state;
    }

    return addSequenceIds(state, sequence.sequenceInstance.telemonitoringId, [sequence.sequenceInstance.id]);
  }),

  on(telemonitoringsActions.loadTelemonitoringSequences, (state: TelemonitoringsState, { tmId, sequences }) => ({
    ...state,
    telemonitorings: {
      ...state.telemonitorings,
      data: {
        ...state.telemonitorings.data,
        [tmId]: {
          ...state.telemonitorings.data[tmId],
          sequenceIds: sequences.map((sequence) => sequence.sequenceInstance.id),
          thumbnailUri: state.telemonitorings.data[tmId]?.thumbnailUri ?? getThumbnailFromSequences(sequences),
        },
      },
    },
  })),

  on(telemonitoringsActions.linkSequencesSuccess, (state: TelemonitoringsState, { telemonitoringId, sequenceIds }) =>
    addSequenceIds(state, telemonitoringId, sequenceIds),
  ),

  on(telemonitoringsActions.unlinkSequenceSuccess, (state: TelemonitoringsState, { telemonitoringId, sequenceId }) => ({
    ...state,
    telemonitorings: {
      ...state.telemonitorings,
      data: {
        ...state.telemonitorings.data,
        [telemonitoringId]: {
          ...state.telemonitorings.data[telemonitoringId],
          sequenceIds: state.telemonitorings.data[telemonitoringId].sequenceIds.filter((id) => id !== sequenceId),
          lastActivity: new Date(Date.now()),
        },
      },
    },
  })),

  on(
    telemonitoringsActions.getTelemonitoringUsersSuccess,
    (state: TelemonitoringsState, { telemonitoringId, userIds }) => ({
      ...state,
      telemonitorings: {
        ...state.telemonitorings,
        data: {
          ...state.telemonitorings.data,
          [telemonitoringId]: {
            ...state.telemonitorings.data[telemonitoringId],
            userIds,
          },
        },
      },
    }),
  ),
);
